import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { create } from 'zustand'
import { WidgetOutlineOverlay } from './widget-outline-overlay'
import { Signal } from '@/utils/signals'
import { usePanelRegistry } from '@/components/editor/panels/panel-registry'
import { stringifyIndicesToWidgetPath, widgetPathToIndices } from '@/components/editor/widget-tree'
import { voteToDisableIframePointerEvents } from '@/helpers/toggle-iframe-events'
import { findAreaElementForPath } from '@/components/editor/widget-dom'
import { useConfigMode } from '@/components/editor/configuration-mode'
import { useSelector } from '@/store'
import { useSettingsOnWidgetStore, WIDGET_EDIT_PANEL } from '@/components/editor/widget-editor/settings-size'

/**
 * This is currently using widget-tree paths like mainArea/0/1/2 since there is currently
 * no good deterministic way to uniquely identify every widget instance.
 *
 * _Caution_: paths are not stable across all types of mutations: e.g. removal or reorder of widgets
 */
type Store = {
  selectedPath: string | null,
  hoveredPath: string | null
}

export const useWidgetSelectorStore = create<Store>()(() => ({
  selectedPath: null,
  hoveredPath: null
}))

export type WidgetSelectorHandle = {
  /** Whether widget mouse selection is started */
  started: () => boolean,

  /** Signal to listen to for start changes */
  startedSignal: Signal<boolean>,

  /** Signal to listen to for selection changes */
  selectedSignal: Signal<string>,

  /** Start widget selection */
  start: () => void

  /** Cancel widget selection */
  cancel: () => void
}

export const WidgetSelector = forwardRef<WidgetSelectorHandle, {}>((_props, ref) => {
  const started = useRef(false)
  const startedSignal = useRef(new Signal<boolean>())
  const selectedSignal = useRef(new Signal<string>())

  const store = useWidgetSelectorStore()
  const configMode = useConfigMode()
  const viewerConfig = useSelector(s => s.currentViewer.config)
  const [ hoveredArea, setHoveredArea ] = useState<HTMLElement | null>(null)
  const [ selectedArea, setSelectedArea ] = useState<HTMLElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const lastHoveredArea = useRef<HTMLElement | null>(null)

  const setStarted = (start: boolean) => {
    started.current = start
    startedSignal.current.emit(start)
    if (!start) setHoveredArea(null)

    // Disable frame widgets events to make them selectable
    voteToDisableIframePointerEvents("widget-selector", start ? "disable" : "enable")
  }

  useImperativeHandle(ref,
    () => ({
      started: () => started.current,
      startedSignal: startedSignal.current,
      selectedSignal: selectedSignal.current,
      start: () => {
        // Timeout as we don't want our _own_ click handler to be triggered at the same time
        setTimeout(() => {
          setStarted(true)
        })
      },
      cancel: () => setStarted(false)
    }),
    [])

  useEffect(function forgetSelectionOfPreviousApp() {
    useWidgetSelectorStore.setState({ selectedPath: null, hoveredPath: null })
  }, [ viewerConfig?.id ])

  // Stop picking (and re-enable iframe events) when leaving the mode that can pick
  useEffect(function stopPickingOutsideEditMode() {
    if (configMode === "edit") return
    if (!started.current) return
    setStarted(false)
  }, [ configMode ])

  useEffect(function releaseIframeVoteOnUnmount() {
    return () => voteToDisableIframePointerEvents("widget-selector", "enable")
  }, [])

  useEffect(function resolveHoveredArea() {
    setHoveredArea(findAreaElementForPath(store.hoveredPath))
  }, [ store.hoveredPath, viewerConfig, configMode ])

  useEffect(function resolveSelectedArea() {
    setSelectedArea(findAreaElementForPath(store.selectedPath))
  }, [ store.selectedPath, viewerConfig, configMode ])

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!started.current) return
      if (!e.target) return
      if (!(e.target instanceof Element)) return
      if (e.target === hoveredArea) return

      const closestOverlay = findAncestor(e.target, e => e === overlayRef.current)
      if (closestOverlay) return

      const closestArea = findAncestor(e.target, e => !!e.dataset.widgetPath) as HTMLElement | null
      if (!closestArea) return

      setHoveredArea(closestArea)
      useWidgetSelectorStore.setState({
        hoveredPath: closestArea.dataset.widgetPath
      })
    }
    const onClick = (e: MouseEvent) => {
      if (!started.current) return
      if (!e.target) return
      if (!(e.target instanceof Element)) return
      const closestOverlay = findAncestor(e.target, e => e === overlayRef.current)
      const closestArea = findAncestor(e.target, e => !!e.dataset.widgetPath)

      const isClickingInsideAppArea = Boolean(closestArea || closestOverlay)
      if (!isClickingInsideAppArea) return

      e.stopPropagation()
      e.preventDefault()

      const store = useWidgetSelectorStore.getState()
      if (store.hoveredPath != null) {
        useWidgetSelectorStore.setState({
          selectedPath: store.hoveredPath,
          hoveredPath: null
        })

        selectedSignal.current.emit(store.hoveredPath)

        // Auto open widget editor after selection, unless the settings show on the widget itself.
        // Maybe this should be handled via signal above, but not sure where yet...
        if (useSettingsOnWidgetStore.getState().onWidget) {
          useSettingsOnWidgetStore.setState({ shown: true })
        } else if (usePanelRegistry.getState().openPanels.size === 0) {
          usePanelRegistry.getState().openPanel(WIDGET_EDIT_PANEL)
        }
      }
      setStarted(false)
    }

    const controller = new AbortController()
    const { signal } = controller
    document.addEventListener("mousemove", onMouseMove, { signal })
    document.addEventListener("click", onClick, { signal, capture: true })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    updateLayoutWidgetSelectHelper(hoveredArea, lastHoveredArea)
  }, [ hoveredArea ])

  if (configMode === "view") return null

  return (
    <div ref={rootRef}>
      {hoveredArea && <WidgetOutlineOverlay ref={overlayRef} variant="hovered"  target={hoveredArea} />}
      {selectedArea && <WidgetOutlineOverlay variant="selected" target={selectedArea} />}
    </div>
  )
})

function updateLayoutWidgetSelectHelper(area: HTMLElement | null, lastArea: React.MutableRefObject<HTMLElement | null>) {
  if (lastArea.current === area) return

  const prevHovers = document.querySelectorAll('[data-edit-hover=true]')
  prevHovers.forEach(e => {
    if (!(e instanceof HTMLElement)) return
    e.dataset.editHover = ''
    e.dataset.editHoverVisible = ''
  })

  lastArea.current = area
  if (!area) return

  const isLayoutWidget = area.dataset.layoutWidget === 'true'

  if (isLayoutWidget) {
    area.dataset.editHover = 'true'
  } else {
    const closestLayoutArea = findAncestor(area.parentElement, e => !!e.dataset.layoutWidget)
    if (!closestLayoutArea) return

    closestLayoutArea.dataset.editHover = 'true'
    closestLayoutArea.dataset.editHoverVisible = 'true'
  }
}

function findAncestor(element: Element | null, condition: (el: HTMLElement) => boolean): HTMLElement | null {
  let current: Element | null = element

  while (current !== null) {
    if (current instanceof HTMLElement && condition(current)) return current
    current = current.parentElement
  }

  return null
}

/**
 * We need special handling for Areas spawned by layout widgets, which do not actually exist in config yet.
 */
export function constructPossiblyUninitializedWidgetPath(params: {
  widgetPath: string | null,
  parentWidgetPath: string | null,
  areaIndex: number | null
}): string {
  if (params.widgetPath) return params.widgetPath
  if (!params.parentWidgetPath || params.areaIndex === null) {
    console.error("area with neither an initialized widget path nor parent path")
    return "invalid"
  }
  const areaIndex = params.areaIndex
  const parentPath = widgetPathToIndices(params.parentWidgetPath)
  const fullPath = [ ...parentPath, areaIndex ]
  return stringifyIndicesToWidgetPath(fullPath)
}
